/**
 * Vehicle type (body type) dropdown options, derived from `GET /v3/vehicleTypes`.
 *
 * Each type belongs to a `vehicleClass` (BIKE, CAR_SUV, …). The preset form renders a grouped
 * menu like fleetconsole; System Administrators add body types under a class (or a custom class).
 */

/** Fallback list used only when the endpoint is unreachable, so the form still renders something. */
export const FALLBACK_VEHICLE_TYPES = [
    { name: 'Light Duty Truck', vehicleClass: 'VAN_LTRUCK' },
    { name: 'Medium Duty Truck', vehicleClass: 'HEAVY_TRUCK' },
    { name: 'Heavy Duty Truck', vehicleClass: 'HEAVY_TRUCK' },
    { name: 'Commercial Bus', vehicleClass: 'BUS' },
    { name: 'SUV', vehicleClass: 'CAR_SUV' },
];

export const FALLBACK_VEHICLE_TYPE_OPTIONS = FALLBACK_VEHICLE_TYPES.map((type) => ({
    value: type.name,
    label: type.name,
    vehicleClass: type.vehicleClass,
}));

/**
 * Default vehicle classes offered when an admin adds a body type (fleetconsole VCLASS_ENUM).
 * These tokens are what we persist as `vehicleClass` on POST /v3/vehicleTypes.
 * Custom classes can still be typed via "+ Add vehicle class".
 */
export const DEFAULT_VEHICLE_CLASSES = [
    { value: 'BIKE', label: 'Bike' },
    { value: 'CAR_SUV', label: 'Car SUV' },
    { value: 'VAN_LTRUCK', label: 'Van Truck' },
    { value: 'HEAVY_TRUCK', label: 'Heavy Truck' },
    { value: 'BUS', label: 'Bus' },
];

const CLASS_LABEL_BY_KEY = DEFAULT_VEHICLE_CLASSES.reduce((acc, row) => {
    acc[row.value] = row.label;
    return acc;
}, { OTHER: 'Other' });

const CLASS_SORT_RANK = {
    BIKE: 1,
    CAR_SUV: 2,
    VAN_LTRUCK: 3,
    HEAVY_TRUCK: 4,
    BUS: 5,
    OTHER: 100,
};

/**
 * Map API / seeded display names onto the fleetconsole class tokens used for grouping
 * and CLASS_BOUNDARY. Seeded rows may come back with vehicleClass equal to the type name
 * (e.g. "Light Duty Truck") until the allow-list is aligned with these tokens.
 */
const CLASS_KEY_ALIASES = {
    BIKE: 'BIKE',
    CAR_SUV: 'CAR_SUV',
    CAR: 'CAR_SUV',
    SUV: 'CAR_SUV',
    VAN_LTRUCK: 'VAN_LTRUCK',
    VAN_TRUCK: 'VAN_LTRUCK',
    LIGHT_DUTY_TRUCK: 'VAN_LTRUCK',
    LIGHT_TRUCK: 'VAN_LTRUCK',
    MEDIUM_DUTY_TRUCK: 'HEAVY_TRUCK',
    MEDIUM_TRUCK: 'HEAVY_TRUCK',
    HEAVY_DUTY_TRUCK: 'HEAVY_TRUCK',
    HEAVY_TRUCK: 'HEAVY_TRUCK',
    COMMERCIAL_BUS: 'BUS',
    BUS: 'BUS',
    OTHER: 'OTHER',
};

/**
 * Sentinel option value for the "+ Add new vehicle type" row. Selecting it opens an input below the
 * select rather than changing the preset's type — same shape as EvTariffModal's 'CUSTOM' preset.
 *
 * Type names are free text, so a name COULD equal a sentinel; `buildVehicleTypeOptions` filters those
 * rows out rather than letting one shadow an action row.
 */
export const ADD_VEHICLE_TYPE_VALUE = '__ADD_VEHICLE_TYPE__';

/** Sentinel for the "Manage vehicle types" row, rendered below the add row. Opens the manage dialog. */
export const MANAGE_VEHICLE_TYPES_VALUE = '__MANAGE_VEHICLE_TYPES__';

/** Sentinel that reveals the inline "new vehicle class" input while adding a body type. */
export const ADD_VEHICLE_CLASS_VALUE = '__ADD_VEHICLE_CLASS__';

/** Unwrap the axios/express envelope: { data: { data: [rows] } }. */
export const extractVehicleTypeData = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const payload = response.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.vehicleTypes)) return payload.vehicleTypes;
    return [];
};

export const normalizeVehicleClassKey = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    const token = trimmed.toUpperCase().replace(/[\s/-]+/g, '_');
    return CLASS_KEY_ALIASES[token] || token;
};

export const formatVehicleClassLabel = (vehicleClass) => {
    const key = normalizeVehicleClassKey(vehicleClass);
    if (!key) return 'Other';
    if (CLASS_LABEL_BY_KEY[key]) return CLASS_LABEL_BY_KEY[key];
    return key.replace(/_/g, ' ');
};

/**
 * `{ value, label, vehicleClass }` shape consumed by the shared `DropDownList`.
 * `value === label` because the literal body-type name string is what gets persisted on the preset.
 */
export const buildVehicleTypeOptions = (apiRows) => {
    if (!Array.isArray(apiRows) || apiRows.length === 0) return [];
    return apiRows
        .map((row) => {
            const name = String(row?.name || '').trim();
            if (!name) return null;
            if (name === ADD_VEHICLE_TYPE_VALUE || name === MANAGE_VEHICLE_TYPES_VALUE) return null;
            return {
                value: name,
                label: name,
                vehicleClass: normalizeVehicleClassKey(row?.vehicleClass),
            };
        })
        .filter(Boolean);
};

/** Flatten grouped or flat option lists (skips group headers). */
const flattenVehicleTypeOptions = (options = []) => {
    const flat = [];
    options.forEach((entry) => {
        if (!entry) return;
        if (Array.isArray(entry.options)) {
            entry.options.forEach((child) => {
                if (child && child.value != null) flat.push(child);
            });
            return;
        }
        if (entry.value != null) flat.push(entry);
    });
    return flat;
};

export const findVehicleTypeOption = (options, value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return null;
    return flattenVehicleTypeOptions(options).find((option) => option.value === trimmed) || null;
};

/**
 * Group body types under vehicle-class headers for the preset dropdown (fleetconsole style).
 */
export const groupVehicleTypeOptions = (flatOptions = []) => {
    if (!Array.isArray(flatOptions) || flatOptions.length === 0) return [];

    const buckets = new Map();
    flatOptions.forEach((option) => {
        const classKey = normalizeVehicleClassKey(option.vehicleClass) || 'OTHER';
        if (!buckets.has(classKey)) buckets.set(classKey, []);
        buckets.get(classKey).push(option);
    });

    return [...buckets.entries()]
        .sort((a, b) => {
            const rankA = CLASS_SORT_RANK[a[0]] || 100;
            const rankB = CLASS_SORT_RANK[b[0]] || 100;
            if (rankA !== rankB) return rankA - rankB;
            return a[0].localeCompare(b[0]);
        })
        .map(([classKey, options]) => ({
            // Fleetconsole-style optgroup headers use the class token itself (BIKE, CAR_SUV, …),
            // deliberately not formatVehicleClassLabel — that stays readable text for the Vehicle
            // Class dropdown and VehicleTypeManagerModal.
            label: classKey,
            options,
        }));
};

/**
 * Keep a saved preset value visible even when it is not in the fetched list — the type may have been
 * deactivated since, or predate the collection. Mirrors `mergeFuelTariffOptions`.
 */
export const mergeVehicleTypeOptions = (options, savedValue) => {
    const trimmed = String(savedValue || '').trim();
    const flat = flattenVehicleTypeOptions(options);
    if (!trimmed) return groupVehicleTypeOptions(flat);
    if (flat.some((option) => option.value === trimmed)) {
        return groupVehicleTypeOptions(flat);
    }
    // Prepended orphan under Other so a deactivated type still displays.
    return groupVehicleTypeOptions([
        { value: trimmed, label: trimmed, vehicleClass: 'OTHER' },
        ...flat,
    ]);
};

/** Class options for the admin add form: defaults + any custom classes already on types. */
export const buildVehicleClassSelectOptions = (flatTypeOptions = [], { includeAdd = false } = {}) => {
    const seen = new Set(DEFAULT_VEHICLE_CLASSES.map((row) => row.value));
    const options = DEFAULT_VEHICLE_CLASSES.map((row) => ({ ...row }));

    flattenVehicleTypeOptions(flatTypeOptions).forEach((type) => {
        const key = normalizeVehicleClassKey(type.vehicleClass);
        if (!key || seen.has(key)) return;
        seen.add(key);
        options.push({ value: key, label: formatVehicleClassLabel(key) });
    });

    if (includeAdd) {
        options.push({ value: ADD_VEHICLE_CLASS_VALUE, label: '+ Add vehicle class' });
    }
    return options;
};

/** Map body-type name → vehicleClass for trip/idle preset resolution. */
export const buildVehicleClassByName = (apiRowsOrOptions = []) => {
    const map = {};
    const rows = Array.isArray(apiRowsOrOptions) ? apiRowsOrOptions : [];
    rows.forEach((row) => {
        const name = String(row?.name || row?.value || '').trim();
        const vehicleClass = normalizeVehicleClassKey(row?.vehicleClass);
        if (name && vehicleClass) map[name] = vehicleClass;
    });
    return map;
};
